import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { UserRole } from '../apis/api-client';
import { authClient, notificationsClient } from '../apis/client-instances';
import { ReactComponent as BackIcon } from '../assets/images/backIcon.svg';
import { useLayoutConfig } from '../contexts/LayoutContext';
import { clearUser, setUser } from '../features/profile/profileSlice';
import { setUnreadCount } from '../features/notifications/notificationsSlice';
import { useWorkspaceBranding } from '../hooks/useWorkspaceBranding';
import { getFileUrl } from '../hooks/function';
import { RootState } from '../types';
import SearchAutocomplete from './common/SearchAutocomplete';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from './ui';
import { EditIcon, LogoutIcon, NotificationsIcon, PersonIcon, VisibilityIcon } from './icons';
import { setViewAsLearner, useViewAsLearner } from '../utils/viewAsLearnerPref';
import NotificationMenu from './notifications/NotificationMenu';
import Logo from '../assets/images/logo.webp';

interface TopbarProps {
  isAuthenticated?: boolean;
  /** True when the left sidebar is rendered (admin views). When false
   *  the topbar drops its 240px left offset so the bar spans the full
   *  width — used on the learner dashboard where the sidebar is hidden. */
  sidebarOffset?: boolean;
}

export default function Topbar({ isAuthenticated = true, sidebarOffset = true }: TopbarProps) {
  const dispatch = useDispatch();
  const unreadCount = useSelector((state: RootState) => state.notifications.unreadCount);
  const currentUser = useSelector((state: RootState) => state.profile.user);
  const { resetToAccountBranding } = useWorkspaceBranding();

  // Account menu — anchored to the avatar on the right of the topbar.
  // Gives learners a way to reach their profile and log out without a
  // sidebar; admins get a redundant-but-convenient shortcut.
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<HTMLElement | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string>('/user.png');

  // Resolve profile image filename → SAS URL for the topbar avatar.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentUser?.profileImageUrl) {
        if (!cancelled) setAvatarUrl('/user.png');
        return;
      }
      try {
        const url = await getFileUrl(currentUser.profileImageUrl);
        if (!cancelled) setAvatarUrl(url || '/user.png');
      } catch {
        if (!cancelled) setAvatarUrl('/user.png');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.profileImageUrl]);

  const handleLogout = () => {
    setAccountMenuAnchor(null);
    dispatch(clearUser());
    localStorage.setItem('token', '');
    localStorage.removeItem('viewAsWorkspaceId');
    resetToAccountBranding();
    navigate('/signin');
  };
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<HTMLElement | null>(null);
  // Ref to anchor the notification menu. Reading event.currentTarget here
  // trips a Firefox + browser-extension bug ("Permission denied to access
  // correspondingUseElement") when the click bubbles through extension-
  // injected SVG content; refs sidestep the synthetic-event entirely.
  const notificationButtonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { layoutConfig } = useLayoutConfig();
  // Learners don't get the global course search — their dashboard
  // exposes their content directly, so a catalog-scoped search at the
  // top is noise.
  const role = currentUser?.role;
  const isLearner = role === UserRole.User;
  const showGlobalSearch = !layoutConfig.hideGlobalSearch && !isLearner;
  // Preview-as-learner pref (shared via utils/viewAsLearnerPref).
  // Lives in the topbar so admins can flip into the learner experience
  // from anywhere — every page mounts this bar. The actual content
  // swap happens on `/courses`, so clicking the button always lands
  // there. Visible for every admin role.
  const viewingAsLearner = useViewAsLearner();
  // The workspace currently pinned in the switcher, used here to refresh branding on
  // Preview-as-learner click. Without this re-apply the admin can drift
  // out of the workspace context (navigation, state churn) and land in
  // the preview with the global account-level look instead of the
  // workspace's.
  const selectedWorkspace = useSelector((state: RootState) => state.workspaces.selectedWorkspace);
  // Tenant logo/name for the topbar wordmark when the sidebar is
  // hidden (learner views). Reads from redux so it picks up workspace
  // overlays applied by the branding utilities.
  const accountLogo = useSelector((state: RootState) => state.account.logoUrl);
  const accountName = useSelector((state: RootState) => state.account.name);
  // Preview affordance gated on having a workspace to preview INTO.
  // - Real WorkspaceAdmins: always — their own workspace is implicit
  //   (AppThemeProvider applied its branding on login).
  // - Account/Super admins: only when a workspace is pinned (via
  //   the switcher). Otherwise the
  //   button would just flip a flag with no workspace context to
  //   show, which is the current confusing behavior on /workspaces.
  const canPreviewAsLearner =
    role === UserRole.WorkspaceAdmin ||
    ((role === UserRole.Admin || role === UserRole.AccountAdmin) &&
      !!selectedWorkspace?.id);

  const fetchMe = async () => {
    try {
      const result = await authClient.me();
      dispatch(setUser(result));
    } catch (error: any) {
      // Stale/cross-tenant token: the token is a valid JWT signature but the
      // user doesn't exist (or doesn't belong to this tenant). Drop it so the
      // next check-session can't falsely re-authenticate the user and trap
      // them in a login loop.
      const status = error?.status ?? error?.response?.status;
      if (status === 401 || status === 404) {
        localStorage.removeItem('token');
        dispatch(clearUser());
      }
      console.error('Failed to fetch user:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationsClient.getUnreadCount();
      const data = await response.data.text();
      const parsed = JSON.parse(data);
      dispatch(setUnreadCount(parsed.unreadCount || 0));
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  useEffect(() => {
    // Always attempt to fetch on mount - API layer handles token validation
    // If no token or invalid, fetch fails gracefully and user stays logged out
    fetchMe();
    fetchUnreadCount();
  }, []);

  const handleNotificationMenuOpen = () => {
    setNotificationAnchorEl(notificationButtonRef.current);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
    // Refresh unread count after closing notification menu
    fetchUnreadCount();
  };

  const handleBackClick = () => {
    if (layoutConfig.backButtonAction) {
      layoutConfig.backButtonAction();
    } else {
      navigate(-1);
    }
  };

  return (
    <AppBar
      elevation={0}
      position="sticky"
      sx={{
        bgcolor: 'white',
        color: 'black',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        width: 'auto',
        left: isAuthenticated && sidebarOffset ? 240 : 0,  // No offset when sidebar is hidden
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        {/* Left side — Dentlogics logo (learner views only; admin
            views show it in the sidebar) plus the optional back
            button. Logo + back sit on the same line as the bell and
            avatar on the right. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {!sidebarOffset && (
            <Box
              component="img"
              // Prefer the tenant's wordmark from redux — set by the
              // branding applier on initial load and on workspace
              // previews. Falls back to the bundled Dentlogics asset
              // when no tenant logo is configured.
              src={accountLogo || Logo}
              alt={accountName || 'Dentlogics'}
              // Clicking the wordmark returns to the learner dashboard.
              // This is a learner's only logo (they have no sidebar), so
              // it's their way out of pages like My Account, which
              // otherwise has no back affordance. Mirrors SidebarLogo.
              onClick={() => navigate('/courses')}
              sx={{ height: 32, width: 'auto', display: 'block', cursor: 'pointer' }}
            />
          )}
          {layoutConfig.showBackButton && (
            <Box
              sx={{
                flexDirection: 'row',
                alignItems: 'center',
                display: 'flex',
                cursor: 'pointer',
                gap: 1,
              }}
              onClick={handleBackClick}
            >
              <BackIcon width={30} height={30} />
              <Box sx={{ fontWeight: 500 }}>{layoutConfig.backButtonText || 'Back'}</Box>
            </Box>
          )}
        </Box>

        {showGlobalSearch && (
          <Box sx={{ maxWidth: 600, width: '100%' }}>
            <SearchAutocomplete />
          </Box>
        )}

        {/* Right side */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isAuthenticated ? (
            <>
              {/* Preview as learner — admin-only, all pages. Sits to
                  the left of the bell so it reads as a session-level
                  affordance (not page-scoped). Clicking always lands
                  on /courses so the swap is visible immediately. */}
              {canPreviewAsLearner && (
                <Button
                  size="small"
                  variant={viewingAsLearner ? 'contained' : 'outlined'}
                  startIcon={viewingAsLearner ? <EditIcon /> : <VisibilityIcon />}
                  onClick={() => {
                    // Workspace branding is now applied per-tab on
                    // mount (see AppThemeProvider Phase 3), so we
                    // don't need a re-apply here — flipping the pref
                    // is enough to toggle the admin-vs-learner view.
                    const next = !viewingAsLearner;
                    setViewAsLearner(next);
                    if (window.location.pathname !== '/courses') navigate('/courses');
                  }}
                  sx={{
                    mr: 0.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: viewingAsLearner ? 'transparent' : 'rgba(0,0,0,0.18)',
                    color: viewingAsLearner ? '#fff' : 'text.primary',
                    ...(viewingAsLearner
                      ? {
                          bgcolor: 'primary.main',
                          '&:hover': { bgcolor: 'primary.dark' },
                        }
                      : {
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'rgba(var(--brand-rgb), 0.08)',
                          },
                        }),
                  }}
                >
                  {viewingAsLearner ? 'Return to admin' : 'Preview as learner'}
                </Button>
              )}
              <IconButton ref={notificationButtonRef} onClick={handleNotificationMenuOpen}>
                <Badge
                  badgeContent={unreadCount}
                  color="error"
                  variant={unreadCount > 0 ? 'standard' : 'dot'}
                  invisible={unreadCount === 0}
                >
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              {/* Account avatar — opens the My Account menu. Sized to
                  match the notifications icon's tap target so the two
                  affordances read as siblings. */}
              <IconButton
                onClick={(e) => setAccountMenuAnchor(e.currentTarget)}
                sx={{ p: 0.5 }}
              >
                <Avatar
                  src={avatarUrl}
                  alt={
                    currentUser
                      ? [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ') || 'Account'
                      : 'Account'
                  }
                  sx={{
                    width: 34,
                    height: 34,
                    border: '2px solid',
                    borderColor: !!accountMenuAnchor ? 'primary.main' : 'transparent',
                    transition: 'border-color 120ms ease',
                  }}
                />
              </IconButton>
            </>
          ) : (
            <Button variant="contained" color="primary" onClick={() => navigate('/signin')}>
              Sign In
            </Button>
          )}
        </Box>

        {/* Account dropdown — name/email header + profile + logout. */}
        <Menu
          anchorEl={accountMenuAnchor}
          open={!!accountMenuAnchor}
          onClose={() => setAccountMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { minWidth: 260, mt: 1, borderRadius: 1.5 } }}
        >
          {currentUser && (
            <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar src={avatarUrl} sx={{ width: 40, height: 40 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700} noWrap>
                  {[currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ') || 'My Account'}
                </Typography>
                {currentUser.email && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {currentUser.email}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          <Divider />
          <MenuItem
            onClick={() => {
              setAccountMenuAnchor(null);
              navigate('/profile');
            }}
          >
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>My Account</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={handleLogout}
            sx={{
              color: 'error.main',
              '&:hover': { bgcolor: 'error.softBg' },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Log out</ListItemText>
          </MenuItem>
        </Menu>

        {/* Notification dropdown */}
        <NotificationMenu
          anchorEl={notificationAnchorEl}
          open={Boolean(notificationAnchorEl)}
          onClose={handleNotificationMenuClose}
        />
      </Toolbar>
    </AppBar>
  );
}
